// src/components/LocationInput.jsx
//
// Text field with live Nominatim autocomplete suggestions (Nigeria-first,
// with a word-drop fallback). Pick a suggestion to resolve it into a
// { lat, lon, name } location via onSelect.
import React, { useState, useEffect, useRef, useCallback } from 'react';

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';

const LocationInput = ({ value, onChange, onSelect, onEnter, placeholder, className = '' }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  // Close the dropdown when clicking outside the field
  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const fetchSuggestions = useCallback(async (query) => {
    if (!query.trim()) {
      setSuggestions([]);
      setNoResults(false);
      return;
    }

    const search = async (q) => {
      const params = new URLSearchParams({
        format: 'json',
        limit: '6',
        addressdetails: '1',
        // Nigeria-first suggestions for a Nigerian marketplace; pick-on-map
        // covers anything outside the country.
        countrycodes: 'ng',
        q,
      });
      const res = await fetch(`${NOMINATIM_URL}?${params.toString()}`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error('Geocoding failed');
      return res.json();
    };

    setSearching(true);
    setNoResults(false);
    try {
      let data = await search(query.trim());
      // Fallback: if nothing matched, drop the last word
      // ("Cariton hotel" -> "Cariton") in case the exact name isn't mapped.
      if (!data.length) {
        const words = query.trim().split(/\s+/);
        if (words.length > 1) {
          data = await search(words.slice(0, -1).join(' '));
        }
      }
      setSuggestions(data);
      setNoResults(data.length === 0);
      setOpen(true);
      setActiveIndex(-1);
    } catch (e) {
      setSuggestions([]);
      setNoResults(false);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    onChange(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 350);
  };

  const handlePick = (hit) => {
    onChange(hit.display_name);
    onSelect({ lat: parseFloat(hit.lat), lon: parseFloat(hit.lon), name: hit.display_name });
    setOpen(false);
    setNoResults(false);
    setActiveIndex(-1);
  };

  const shortAddress = (hit) => {
    const a = hit.address || {};
    return [a.city || a.town || a.village || a.suburb || '', a.state || a.country || ''].filter(Boolean).join(', ');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown' && open && suggestions.length) {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp' && open && suggestions.length) {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Escape') {
      setOpen(false);
    } else if (e.key === 'Enter') {
      if (open && activeIndex >= 0 && suggestions[activeIndex]) {
        e.preventDefault();
        handlePick(suggestions[activeIndex]);
      } else if (onEnter) {
        onEnter();
      }
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <input
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        className={`w-full border border-gray-300 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${className}`}
      />
      {open && (
        <div className="absolute z-30 mt-1.5 w-full bg-white rounded-xl shadow-lg border border-gray-200 max-h-64 overflow-y-auto">
          {searching && (
            <div className="px-3.5 py-2.5 text-sm text-gray-400 flex items-center gap-2">
              <span className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-blue-600" />
              Searching...
            </div>
          )}
          {!searching && noResults && (
            <div className="px-3.5 py-2.5 text-sm text-gray-500">
              No results in Nigeria for that. Try a different spelling, or tap
              the field's “Pick on map” option to drop the pin yourself.
            </div>
          )}
          {!searching &&
            suggestions.map((hit, idx) => (
              <button
                key={hit.place_id || idx}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handlePick(hit);
                }}
                onMouseEnter={() => setActiveIndex(idx)}
                className={`w-full text-left px-3.5 py-2.5 text-sm transition-colors ${
                  idx === activeIndex ? 'bg-blue-50' : 'hover:bg-gray-50'
                }`}
              >
                <span className="block text-gray-800 truncate">{hit.display_name}</span>
                {shortAddress(hit) && (
                  <span className="block text-xs text-gray-500 truncate">{shortAddress(hit)}</span>
                )}
              </button>
            ))}
        </div>
      )}
    </div>
  );
};

export default LocationInput;
